import { useState } from "react";
import { ChartOfAccountsTemplateUploadSource } from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import { ChartOfAccountsTemplateUploadResponse } from "@common/schemas/chart-of-accounts-template-upload.schema";
import { TypedChartOfAccountsTemplateUpload } from "@common/helpers/chartOfAccountsTemplateTypeMapping";

export interface ChartOfAccountsTemplateUploadDraft {
    source: ChartOfAccountsTemplateUploadSource;
    fileName: string;
    chart: ChartOfAccountsTemplateUploadResponse;
}

export type ChartOfAccountsTemplateUploadStep =
    | { name: "source" }
    | {
          name: "upload";
          source: ChartOfAccountsTemplateUploadSource;
          draft: ChartOfAccountsTemplateUploadDraft | null;
      }
    | { name: "typeMapping"; chart: ChartOfAccountsTemplateUploadResponse }
    | { name: "roleSelection"; chart: TypedChartOfAccountsTemplateUpload };

export function useChartOfAccountsTemplateUploadFlow() {
    const [step, setStep] = useState<ChartOfAccountsTemplateUploadStep>({
        name: "source",
    });
    const [isDiscardPromptOpen, setIsDiscardPromptOpen] = useState(false);

    const hasImportedAccounts =
        step.name === "upload" ? step.draft !== null : step.name !== "source";

    return {
        step,
        isDiscardPromptOpen,
        chooseSource: (source: ChartOfAccountsTemplateUploadSource) =>
            setStep({ name: "upload", source, draft: null }),
        replaceDraft: (nextDraft: ChartOfAccountsTemplateUploadDraft | null) =>
            setStep((current) =>
                current.name === "upload"
                    ? { ...current, draft: nextDraft }
                    : current,
            ),
        continueToTypeMapping: (chart: ChartOfAccountsTemplateUploadResponse) =>
            setStep({ name: "typeMapping", chart }),
        continueToRoleSelection: (chart: TypedChartOfAccountsTemplateUpload) =>
            setStep({ name: "roleSelection", chart }),
        requestClose: (onClose: () => void) => {
            if (hasImportedAccounts) {
                setIsDiscardPromptOpen(true);
                return;
            }
            onClose();
        },
        closeDiscardPrompt: () => setIsDiscardPromptOpen(false),
    };
}

export type ChartOfAccountsTemplateUploadFlowState = ReturnType<
    typeof useChartOfAccountsTemplateUploadFlow
>;
